import { useCallback, useMemo } from "react";

import { type ApolloCache, type FetchResult } from "@apollo/client";
import { type Annotation } from "@features/custom-annotations";
import {
  CREATE_MMM_CUSTOM_ANNOTATION,
  DELETE_MMM_CUSTOM_ANNOTATION,
  FETCH_MMM_CUSTOM_ANNOTATIONS,
  FETCH_MMM_CUSTOM_ANNOTATION_CAMPAIGN_TYPES,
  UPDATE_MMM_CUSTOM_ANNOTATION,
} from "@gql/mmm-custom-annotations";
import {
  useNorthbeamLazyQuery,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import {
  type CreateMmmCustomAnnotation,
  type CreateMmmCustomAnnotationVariables,
  type DeleteMmmCustomAnnotation,
  type DeleteMmmCustomAnnotationVariables,
  type FetchMmmCustomAnnotationCampaignTypes,
  type FetchMmmCustomAnnotations,
  type FetchMmmCustomAnnotationsVariables,
  type UpdateMmmCustomAnnotation,
  type UpdateMmmCustomAnnotationVariables,
} from "@north-beam/nb-client-side";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";

export const useMmmAnnotations = (
  {
    onCreateAnnotation,
  }: {
    onCreateAnnotation:
      | ((
          cache: ApolloCache<CreateMmmCustomAnnotation>,
          result: FetchResult<CreateMmmCustomAnnotation, unknown>,
        ) => void)
      | undefined;
  } = { onCreateAnnotation: undefined },
) => {
  const [modelMetadata] = useModelMetadata();

  const {
    loadData: fetchAnnotations,
    data: { loading: isAnnotationsLoading, data },
  } = useNorthbeamLazyQuery<
    FetchMmmCustomAnnotations,
    FetchMmmCustomAnnotationsVariables
  >(FETCH_MMM_CUSTOM_ANNOTATIONS);

  const annotations = useMemo(() => {
    let result =
      (data?.me?.mmm?.customAnnotations
        ?.customAnnotations as unknown as Annotation[]) ?? ([] as Annotation[]);

    if (modelMetadata?.coe) {
      const coeSet = new Set<string>();

      modelMetadata.coe.forEach((e) =>
        coeSet.add(
          `${e.description} ${e.campaign_type} ${e.start_date} ${e.end_date}`,
        ),
      );

      result = result.map((e) => {
        const key = `${e.description} ${e.campaignType} ${e.annotatedDate} ${e.endDate}`;

        if (coeSet.has(key)) {
          return { ...e, isUsedForTraining: true };
        } else {
          return e;
        }
      });
    }

    return result;
  }, [data, modelMetadata]);

  const { data: annotationCampaignTypes, loading: isCampaignTypesLoading } =
    useNorthbeamQuery<FetchMmmCustomAnnotationCampaignTypes>(
      FETCH_MMM_CUSTOM_ANNOTATION_CAMPAIGN_TYPES,
    );
  const campaignTypes = useMemo(
    () =>
      annotationCampaignTypes?.me?.mmm?.customAnnotations?.campaignTypes ?? [],
    [annotationCampaignTypes],
  );

  const [createAnnotationMutation, { loading: isCreateAnnotationLoading }] =
    useNorthbeamMutation<
      CreateMmmCustomAnnotation,
      CreateMmmCustomAnnotationVariables
    >(CREATE_MMM_CUSTOM_ANNOTATION, {
      update: onCreateAnnotation,
    });

  const createAnnotation = useCallback(
    async (
      description: string,
      annotatedDate: string,
      endDate?: string,
      campaignType?: string,
    ) =>
      createAnnotationMutation({
        variables: {
          description,
          annotatedDate,
          endDate: endDate!,
          campaignType: campaignType!,
        },
      }),
    [createAnnotationMutation],
  );

  const [updateAnnotationMutation, { loading: isUpdateAnnotationLoading }] =
    useNorthbeamMutation<
      UpdateMmmCustomAnnotation,
      UpdateMmmCustomAnnotationVariables
    >(UPDATE_MMM_CUSTOM_ANNOTATION);

  const updateAnnotation = useCallback(
    async (
      id: string,
      description: string,
      annotatedDate: string,
      endDate: string,
      campaignType: string,
    ) =>
      updateAnnotationMutation({
        variables: {
          id,
          description,
          annotatedDate: annotatedDate!,
          endDate: endDate!,
          campaignType: campaignType!,
        },
      }),
    [updateAnnotationMutation],
  );

  const [deleteAnnotationMutation, { loading: isDeleteAnnotationLoading }] =
    useNorthbeamMutation<
      DeleteMmmCustomAnnotation,
      DeleteMmmCustomAnnotationVariables
    >(DELETE_MMM_CUSTOM_ANNOTATION);

  const deleteAnnotation = useCallback(
    async (id: string) => deleteAnnotationMutation({ variables: { id } }),
    [deleteAnnotationMutation],
  );

  return {
    fetchAnnotations,
    annotations,
    campaignTypes,
    isCampaignTypesLoading,
    isAnnotationsLoading:
      isAnnotationsLoading ||
      isCreateAnnotationLoading ||
      isUpdateAnnotationLoading ||
      isDeleteAnnotationLoading,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  };
};
